// HistoryReconcilerProvider — layout suite, browser-history reconciliation
// for modal layers.
//
// Owns: a synthetic browser-history depth that mirrors open modals so the OS
// back gesture closes them without navigating away. Product navigation has
// its own browser entries (with `?viewing=` URLs) managed by
// SessionDataProvider — those entries carry no sentinel and are passed
// through transparently here.

'use client';

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import type { HistoryReconcilerContextValue } from './history-reconciler-types';
import { useHistoryReconcilerValue } from './use-history-reconciler-value';

const HistoryReconcilerContext = createContext<HistoryReconcilerContextValue | null>(null);

interface HistoryReconcilerProviderProps {
  children: ReactNode;
  ownerKey: string;
}

export function HistoryReconcilerProvider({ children, ownerKey }: HistoryReconcilerProviderProps): ReactNode {
  const value = useHistoryReconcilerValue({ ownerKey });

  return (
    <HistoryReconcilerContext.Provider value={value}>
      {children}
    </HistoryReconcilerContext.Provider>
  );
}

export function useHistoryReconciler(): HistoryReconcilerContextValue {
  const context = useContext(HistoryReconcilerContext);
  if (!context) {
    throw new Error('useHistoryReconciler must be used within HistoryReconcilerProvider');
  }

  return context;
}
